import React, { useState, useEffect } from 'react';
import {
  Card,
  Title,
  Text,
  Grid,
  Col,
  Flex,
  Badge,
  BarChart,
  LineChart,
  DonutChart,
  Button,
  DateRangePicker,
  DateRangePickerValue,
  Select,
  SelectItem,
  Divider,
  Metric
} from '@tremor/react';
import {
  RiCalendarLine,
  RiUserLine,
  RiAlarmWarningLine,
  RiLineChartLine,
  RiRefreshLine,
  RiDownloadLine
} from '@remixicon/react';
import { reportService } from '../../services/report';
import { useReportData } from '../../hooks/useReportData';

interface DashboardMetrics {
  totalEmployees: number;
  totalAbsences: number;
  averageBradfordScore: number;
  highRiskEmployees: number;
  absenteeismRate: number;
  leaveUtilization: number;
}

interface TrendData {
  month: string;
  absences: number;
  avgBradford: number;
  leaveBalance: number;
}

const SummaryDashboard: React.FC = () => {
  const reportData = useReportData();
  
  // State for filters
  const [dateRange, setDateRange] = useState<DateRangePickerValue>({
    from: new Date(new Date().getFullYear(), 0, 1), // Start of current year
    to: new Date(),
  });
  const [selectedDepartment, setSelectedDepartment] = useState<string>('all');
  
  // State for dashboard data
  const [metrics, setMetrics] = useState<DashboardMetrics>({
    totalEmployees: 0,
    totalAbsences: 0,
    averageBradfordScore: 0,
    highRiskEmployees: 0,
    absenteeismRate: 0,
    leaveUtilization: 0
  });
  
  const [trendData, setTrendData] = useState<TrendData[]>([]);
  const [departmentData, setDepartmentData] = useState<any[]>([]);
  const [riskDistribution, setRiskDistribution] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch all dashboard data
  const fetchDashboardData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Fetch multiple reports in parallel
      const [bradfordResponse, trendsResponse, balanceResponse] = await Promise.allSettled([
        reportService.getBradfordFactorData({
          department: selectedDepartment !== 'all' ? selectedDepartment : undefined,
          startDate: dateRange.from?.toISOString().split('T')[0],
          endDate: dateRange.to?.toISOString().split('T')[0],
        }),
        reportService.getAbsenceTrendsData({
          startDate: dateRange.from?.toISOString().split('T')[0],
          endDate: dateRange.to?.toISOString().split('T')[0],
          department: selectedDepartment !== 'all' ? selectedDepartment : undefined,
          groupBy: 'month',
          viewType: 'frequency'
        }),
        reportService.getLeaveBalanceReport({
          startDate: dateRange.from?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
          endDate: dateRange.to?.toISOString().split('T')[0] || new Date().toISOString().split('T')[0],
          department: selectedDepartment !== 'all' ? selectedDepartment : undefined,
        })
      ]);

      // Process Bradford Factor data
      if (bradfordResponse.status === 'fulfilled' && bradfordResponse.value.status === 'GENERATE_REPORT_SUCCESS') {
        const bradfordData = bradfordResponse.value.payload.bradford_factor;
        
        // Calculate key metrics
        const totalEmployees = bradfordData.summary.totalEmployees;
        const avgBradford = bradfordData.summary.averageBradfordScore;
        const highRisk = bradfordData.summary.riskDistribution.high + bradfordData.summary.riskDistribution.critical;
        
        setMetrics(prev => ({
          ...prev,
          totalEmployees,
          averageBradfordScore: avgBradford,
          highRiskEmployees: highRisk
        }));

        // Set department comparison data
        setDepartmentData(bradfordData.departmentAverages.map((dept: any) => ({
          department: dept.department,
          'Average Bradford Score': dept.averageScore,
          'Employee Count': dept.employeeCount
        })));

        // Set risk distribution
        setRiskDistribution([
          { name: 'Low Risk', value: bradfordData.summary.riskDistribution.low, color: 'emerald' },
          { name: 'Medium Risk', value: bradfordData.summary.riskDistribution.medium, color: 'amber' },
          { name: 'High Risk', value: bradfordData.summary.riskDistribution.high, color: 'orange' },
          { name: 'Critical Risk', value: bradfordData.summary.riskDistribution.critical, color: 'rose' }
        ]);
      }

      // Process Absence Trends data
      if (trendsResponse.status === 'fulfilled' && trendsResponse.value.status === 'ABSENCE_TRENDS_SUCCESS') {
        const trendsData = trendsResponse.value.payload.absence_trends;
        
        setMetrics(prev => ({
          ...prev,
          totalAbsences: trendsData.metrics.totalAbsences,
          absenteeismRate: prev.totalEmployees > 0 ? (trendsData.metrics.totalAbsences / prev.totalEmployees) * 100 : 0
        }));

        // Process monthly trend data
        const monthlyData = trendsData.chartData.map((item: any) => ({
          month: item.name,
          absences: item.Absences || 0,
          avgBradford: 0, // Will be filled from other data if available
          leaveBalance: 0 // Will be filled from leave balance data
        }));
        
        setTrendData(monthlyData);
      }

      // Process Leave Balance data
      if (balanceResponse.status === 'fulfilled' && balanceResponse.value.status === 'LEAVE_BALANCE_SUCCESS') {
        const leaveData = balanceResponse.value.payload.leave_balance;
        
        // Calculate overall leave utilization
        const totalEntitled = Object.values(leaveData.summary.leaveTypeDistribution)
          .reduce((sum: number, type: any) => sum + type.totalEntitled, 0);
        const totalUsed = Object.values(leaveData.summary.leaveTypeDistribution)
          .reduce((sum: number, type: any) => sum + type.totalUsed, 0);
        
        const utilization = totalEntitled > 0 ? (totalUsed / totalEntitled) * 100 : 0;
        
        setMetrics(prev => ({
          ...prev,
          leaveUtilization: utilization
        }));
      }

    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError('Failed to load dashboard data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Reload data when filters change
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    fetchDashboardData();
  }, [dateRange, selectedDepartment]);
  /* eslint-enable react-hooks/exhaustive-deps */

  // Reload employees when department changes
  useEffect(() => {
    if (selectedDepartment !== 'all') {
      reportData.refetch.employees(selectedDepartment);
    }
  }, [selectedDepartment, reportData.refetch]);

  const formatPercentage = (value: number) => `${value.toFixed(1)}%`;
  const formatNumber = (value: number) => value.toLocaleString();

  return (
    <div className="p-4">
      <Flex justifyContent="between" alignItems="center" className="mb-6">
        <Title>HR Dashboard - Executive Summary</Title>
        <Flex justifyContent="end" className="gap-2">
          <Button
            size="sm"
            variant="secondary"
            icon={RiRefreshLine}
            onClick={fetchDashboardData}
            loading={loading}
          >
            Refresh
          </Button>
          <Button
            size="sm"
            variant="secondary"
            icon={RiDownloadLine}
            onClick={() => {
              // Export dashboard logic could be implemented here
              console.log('Export dashboard functionality');
            }}
          >
            Export
          </Button>
        </Flex>
      </Flex>

      {error && (
        <Card className="mb-6 bg-red-50">
          <Text color="red">{error}</Text>
        </Card>
      )}

      {/* Filter Controls */}
      <Card className="mb-6">
        <Title>Filters</Title>
        <Grid numItems={1} numItemsSm={2} className="gap-4 mt-4">
          <Col>
            <Text className="mb-2">Date Range</Text>
            <DateRangePicker
              className="w-full"
              value={dateRange}
              onValueChange={setDateRange}
              enableSelect={false}
              selectPlaceholder="Select range"
            />
          </Col>
          <Col>
            <Text className="mb-2">Department</Text>
            <Select
              value={selectedDepartment}
              onValueChange={setSelectedDepartment}
              placeholder="Select Department"
              disabled={reportData.loading.departments}
            >
              {reportData.departments.map((dept) => (
                <SelectItem key={dept.id} value={dept.id}>
                  {dept.name}
                </SelectItem>
              ))}
            </Select>
          </Col>
        </Grid>
      </Card>

      {loading ? (
        <Card className="h-64 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
          <Text className="ml-4">Loading dashboard data...</Text>
        </Card>
      ) : (
        <>
          {/* Key Metrics Cards */}
          <Grid numItems={1} numItemsSm={2} numItemsLg={4} className="gap-4 mb-6">
            <Card decoration="top" decorationColor="blue">
              <Flex justifyContent="between">
                <div>
                  <Text>Total Employees</Text>
                  <Metric>{formatNumber(metrics.totalEmployees)}</Metric>
                </div>
                <RiUserLine className="h-10 w-10 text-blue-500" />
              </Flex>
            </Card>

            <Card decoration="top" decorationColor="amber">
              <Flex justifyContent="between">
                <div>
                  <Text>Total Absences</Text>
                  <Metric>{formatNumber(metrics.totalAbsences)}</Metric>
                  <Text className="text-xs">
                    Rate: {formatPercentage(metrics.absenteeismRate)}
                  </Text>
                </div>
                <RiCalendarLine className="h-10 w-10 text-amber-500" />
              </Flex>
            </Card>

            <Card decoration="top" decorationColor="rose">
              <Flex justifyContent="between">
                <div>
                  <Text>High Risk Employees</Text>
                  <Metric>{formatNumber(metrics.highRiskEmployees)}</Metric>
                  <Text className="text-xs">
                    Bradford Score: {formatNumber(metrics.averageBradfordScore)}
                  </Text>
                </div>
                <RiAlarmWarningLine className="h-10 w-10 text-rose-500" />
              </Flex>
            </Card>

            <Card decoration="top" decorationColor="green">
              <Flex justifyContent="between">
                <div>
                  <Text>Leave Utilization</Text>
                  <Metric>{formatPercentage(metrics.leaveUtilization)}</Metric>
                  <Text className="text-xs">Year to date</Text>
                </div>
                <RiLineChartLine className="h-10 w-10 text-green-500" />
              </Flex>
            </Card>
          </Grid>

          {/* Charts Section */}
          <Grid numItems={1} numItemsLg={2} className="gap-6 mb-6">
            {/* Risk Distribution */}
            <Card>
              <Title>Employee Risk Distribution</Title>
              <DonutChart
                className="mt-6"
                data={riskDistribution}
                category="value"
                index="name"
                valueFormatter={(value) => `${value} employees`}
                colors={['emerald', 'amber', 'orange', 'rose']}
              />
            </Card>

            {/* Department Comparison */}
            <Card>
              <Title>Bradford Score by Department</Title>
              <BarChart
                className="mt-6"
                data={departmentData}
                index="department"
                categories={['Average Bradford Score']}
                colors={['blue']}
                valueFormatter={(value) => value.toFixed(1)}
              />
            </Card>
          </Grid>

          {/* Trend Analysis */}
          {trendData.length > 0 && (
            <Card>
              <Title>Monthly Absence Trends</Title>
              <Text>Track absence patterns over time</Text>
              <LineChart
                className="mt-6 h-80"
                data={trendData}
                index="month"
                categories={['absences']}
                colors={['blue']}
                valueFormatter={(value) => `${value} absences`}
                yAxisWidth={48}
              />
            </Card>
          )}

          {/* Quick Insights */}
          <Card className="mt-6">
            <Title>Key Insights</Title>
            <Divider className="my-4" />
            <Grid numItems={1} numItemsSm={2} className="gap-4">
              <div>
                <Text className="font-medium">Risk Analysis</Text>
                <Text className="text-sm mt-2">
                  {metrics.highRiskEmployees > 0 ? (
                    <>
                      <Badge color="rose" className="mr-2">Alert</Badge>
                      {metrics.highRiskEmployees} employees require immediate attention due to high absence patterns.
                    </>
                  ) : (
                    <>
                      <Badge color="green" className="mr-2">Good</Badge>
                      No employees currently in high-risk category.
                    </>
                  )}
                </Text>
              </div>
              <div>
                <Text className="font-medium">Leave Management</Text>
                <Text className="text-sm mt-2">
                  {metrics.leaveUtilization > 75 ? (
                    <>
                      <Badge color="amber" className="mr-2">Watch</Badge>
                      High leave utilization at {formatPercentage(metrics.leaveUtilization)}. Monitor carry-over policies.
                    </>
                  ) : metrics.leaveUtilization < 50 ? (
                    <>
                      <Badge color="blue" className="mr-2">Info</Badge>
                      Low leave utilization at {formatPercentage(metrics.leaveUtilization)}. Encourage work-life balance.
                    </>
                  ) : (
                    <>
                      <Badge color="green" className="mr-2">Optimal</Badge>
                      Healthy leave utilization at {formatPercentage(metrics.leaveUtilization)}.
                    </>
                  )}
                </Text>
              </div>
            </Grid>
          </Card>
        </>
      )}
    </div>
  );
};

export default SummaryDashboard;